import { Platform } from '@angular/cdk/platform';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute, RouterModule } from '@angular/router';
import {
  loadStripe,
  Stripe,
  StripeAddressElement,
  StripeElements,
} from '@stripe/stripe-js';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import isEmail from 'validator/lib/isEmail';
import { environment } from '../../../../environments/environment';
import { SelectUserShippingAddressButton } from '../../../components/account/select-user-shipping-address-button/select-user-shipping-address-button';
import { Button } from '../../../components/common/button/button';
import { Logo } from '../../../components/common/logo/logo';
import { Price } from '../../../components/common/price/price';
import { SiteName } from '../../../components/common/site-name/site-name';
import { CheckoutSummary } from '../../../components/shopping/checkout-summary/checkout-summary';
import {
  UserPaymentIntent,
  UserShippingAddress,
} from '../../../models/shopping';
import { User } from '../../../models/user';
import { ShoppingApiService } from '../../../services/apis/shopping-api.service';
import { ApiException, ApiStatus } from '../../../services/apis/status';
import { AuthService } from '../../../services/auth.service';
import { MessageService } from '../../../services/message.service';
import { TitleService } from '../../../services/title.service';
@Component({
  selector: 'app-checkout-page',
  imports: [
    SiteName,
    Logo,
    RouterModule,
    MatButtonModule,
    MatIconModule,
    RouterModule,
    MatInputModule,
    FormsModule,
    RouterModule,
    NzSkeletonModule,
    Button,
    MatExpansionModule,
    MatDividerModule,
    CheckoutSummary,
    Price,
    SelectUserShippingAddressButton,
  ],
  templateUrl: './checkout-page.html',
  styleUrl: './checkout-page.scss',
})
export class CheckoutPage implements OnInit {
  stripe: Stripe | null = null;
  stripeElements: StripeElements | null = null;
  stripeAddressElement: StripeAddressElement | null = null;
  email: string = '';
  id: string = '';
  user_payment_intent?: UserPaymentIntent;
  @ViewChild('payment') paymentElement!: ElementRef<HTMLElement>;
  @ViewChild('shippingAddress')
  shippingAddressElement!: ElementRef<HTMLElement>;
  constructor(
    private titleService: TitleService,
    private shoppingApi: ShoppingApiService,
    private platform: Platform,
    private messageService: MessageService,
    private route: ActivatedRoute,
    private authService: AuthService
  ) {
    this.titleService.setTitle('Checkout');
    this.id = this.route.snapshot.params['id'];
  }

  loading: boolean = false;
  ngOnInit(): void {
    this.authService.getProfile().then((r) => {
      this.email = r?.email ?? '';
    });
    if (this.platform.isBrowser) {
      this.createPaymentIntent();
    }
  }

  get profile(): User | null {
    return this.authService.profile();
  }

  async createPaymentIntent() {
    try {
      this.loading = true;
      this.stripe = await loadStripe(environment.stripe.apikey);
      if (!this.stripe) {
        return;
      }

      this.user_payment_intent = await this.shoppingApi.getPaymentIntent(
        this.id
      );
      this.stripeElements = this.stripe!.elements({
        clientSecret: this.user_payment_intent.payment_intent_client_secret,
        appearance: {
          theme: 'stripe', // 可用的主题还有 'flat', 'night', 'none'
        },
      });
      this.stripeElements
        .create('payment', {
          layout: 'auto',
          fields: {
            billingDetails: {
              name: 'auto',
              email: 'auto',
              address: {
                country: 'auto',
                postalCode: 'auto',
                state: 'auto',
                city: 'auto',
                line1: 'auto',
                line2: 'auto',
              },
            },
          },
        })
        .mount(this.paymentElement.nativeElement);
      this.stripeAddressElement = this.stripeElements.create('address', {
        mode: 'shipping',
        allowedCountries: ['US'],
        autocomplete: { mode: 'automatic' },
        display: { name: 'split' },
      });
      this.stripeAddressElement.mount(
        this.shippingAddressElement.nativeElement
      );
    } catch (error) {
      if (error instanceof ApiException) {
        if (error.status === ApiStatus.PAYMENT_INTENT_NOT_FOUND) {
          this.messageService.open('Payment Intent Not Found');
        }
      }
      console.log(error);
    } finally {
      this.loading = false;
    }
  }

  onAddressSelected(addr: UserShippingAddress) {
    if (!addr) {
      return;
    }
    if (this.stripeAddressElement) {
      this.stripeAddressElement.unmount();
      this.stripeAddressElement.destroy(); // 👈 必须加 destroy
      this.stripeAddressElement = null;
    }
    this.stripeAddressElement = this.stripeElements!.create('address', {
      mode: 'shipping',
      allowedCountries: ['US'],
      autocomplete: { mode: 'automatic' },
      display: { name: 'split' },
      defaultValues: {
        firstName: addr.fname,
        lastName: addr.lname,
        address: {
          line1: addr.line1,
          line2: addr.line2,
          city: addr.city,
          state: addr.state,
          country: addr.country,
          postal_code: addr.postal_code,
        },
      },
    });
    this.stripeAddressElement.mount(this.shippingAddressElement.nativeElement);
  }

  async pay() {
    if (!this.stripe || !this.stripeElements || !this.user_payment_intent)
      return;
    this.email = this.email.trim();
    if (!this.email) {
      this.messageService.open('Contact Email is required.');
      return;
    } else if (!isEmail(this.email)) {
      this.messageService.open('Contact Email is invalid.');
      return;
    }

    this.loading = true;
    const { error } = await this.stripe.confirmPayment({
      elements: this.stripeElements,
      confirmParams: {
        return_url: `${environment.host}/checkout/result`,
        receipt_email: this.email, // 👈 把 email 传给 Stripe
      },
    });
    this.loading = false;

    if (error) {
      alert(error.message);
    }
  }
}
